/**
 * Order tracking.
 *
 * The customer's side of the collection handshake. Nothing here polls a status
 * flag of its own: a realtime subscription on the `orders` row triggers a refetch
 * through `get_order`, so the moment a merchant scans the customer's QR the
 * screen moves to "Collected" with no extra plumbing.
 *
 * The pickup QR encodes an HMAC-signed `YMP1` token that only this order's owner
 * can read (RLS on `orders`), and which the server verifies before completing
 * anything — a screenshot of someone else's code is worthless.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { View } from 'react-native';
import {
  acceptGraceAnchor,
  canCustomerCancel,
  DEFAULT_ACCEPT_GRACE_SECONDS,
  formatCountdownCoarse,
  formatDayTime,
  formatMoney,
  mapsUrl,
  pickupSteps,
  scheduledNotice,
  telUrl,
  totalItemCount,
  type MessageKey,
  type Order,
  type OrderStatus,
} from '@yummy/shared';
import {
  cancelMyOrder,
  completeMyOrder,
  fetchOrder,
  fetchOrderByCode,
  subscribeToOrder,
} from '@yummy/db';
import {
  Button,
  Callout,
  Card,
  ConfirmDialog,
  CountdownProgress,
  ErrorBanner,
  LoadingState,
  QrCode,
  Row,
  Screen,
  Section,
  Stack,
  StatRow,
  StatusChip,
  ToneBadge,
  OrderCode,
  TONE_TEXT,
  Text,
  cn,
  useAction,
  useAsync,
  useNow,
  useT,
  useToast,
} from '@yummy/ui-rnr';

import { AppBar } from '../components/AppBar';
import { openContactUrl } from '../components/ContactRow';
import { ContactRow } from '../components/ContactRow';
import { useBackTo } from '../app/navigation';
import { FullScreenMessage } from '../components/FullScreenMessage';
import { VerifiedMark } from '../components/VerifiedMark';
import { supabase } from '../lib/supabase';
import { CircleHelp, MapPin, Phone } from 'lucide-react-native';


/**
 * Clearance between the floating header and the status card, in points.
 *
 * Taken from My orders rather than from this screen's own card rhythm, so the
 * first card lands at the same height on both: they are the same shape, a
 * floating bar over a card, and a card that starts lower on one than the other
 * reads as a difference in the bar rather than in the list. The 24pt the cards
 * keep between themselves is a rhythm inside the list, not one across screens.
 */
const HEADER_GAP = 2.5;

/**
 * The headline for each status, for the states a live order can be in.
 *
 * Total over `OrderStatus` on purpose: the previous chain of ternaries ended in
 * `preparing`, so both `pending` and `accepted` were announced as "Being
 * prepared" while the status chip beside them read "Accepted". A missing entry
 * is a type error now instead of a wrong sentence.
 */
const HEADLINE_KEYS: Record<OrderStatus, MessageKey> = {
  pending: 'customer.order.pending',
  accepted: 'customer.order.accepted',
  preparing: 'customer.order.preparing',
  ready: 'customer.order.readyTitle',
  completed: 'customer.order.collected',
  cancelled: 'customer.order.cancelled',
};

/** Progress-timeline labels, keyed by the step identifier `pickupSteps` returns. */
const STEP_LABEL_KEYS = {
  placed: 'customer.order.stepPlaced',
  accepted: 'customer.order.stepAccepted',
  preparing: 'customer.order.stepPreparing',
  ready: 'customer.order.stepReady',
  completed: 'customer.order.stepCompleted',
} as const;

/** Card edge tone, mapped to whole literal classes. */
const CARD_ACCENT = {
  success: 'border-l-success border-l-[3px]',
  danger: 'border-l-destructive border-l-[3px]',
  brand: 'border-l-primary border-l-[3px]',
} as const;

/**
 * Tracks one order, by id or by the six-character code the counter read back.
 *
 * @returns The order tracking screen.
 */
export function OrderScreen() {
  const { orderId, code } = useParams<{ orderId?: string; code?: string }>();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  // Back to wherever the order was opened from: the list, the menu behind a
  // freshly placed order, or the landing page a code was typed into. Only an
  // order opened as the first thing in the session falls back to the list.
  const goBack = useBackTo('/orders');
  const orderState = location.state as { from?: string; listFrom?: string } | null;
  /**
   * Whether this order was opened from the list, which the list says when it
   * pushes it. The way back is then a step back, not another list.
   */
  const cameFromOrders = orderState?.from === 'orders';
  /**
   * How many screens back the menu already is: one when this order took the
   * checkout's place over the menu, two when the list it was opened from was
   * itself opened from the menu, and zero when the menu is not behind it at all.
   */
  const menuDepth = orderState?.from === 'menu' ? 1 : orderState?.listFrom === 'menu' ? 2 : 0;
  const toast = useToast();
  const cancel = useAction();
  const complete = useAction();

  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [resolvedId, setResolvedId] = useState<string | null>(orderId ?? null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // `/order/code/ABC123` is the recovery path for a customer who only has the
  // six characters the counter read back to them.
  useEffect(() => {
    if (orderId || !code) return;
    let cancelled = false;

    void (async () => {
      try {
        const found = await fetchOrderByCode(supabase, code);
        if (cancelled) return;
        if (!found) {
          setResolveError(t('customer.order.notFoundByCode', { code: code.toUpperCase() }));
        } else setResolvedId(found.id);
      } catch (cause) {
        if (!cancelled) {
          setResolveError(cause instanceof Error ? cause.message : String(cause));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orderId, code, t]);

  const order = useAsync<Order | null>(
    async () => (resolvedId ? fetchOrder(supabase, resolvedId) : null),
    [resolvedId],
  );

  const reload = order.reload;

  // ---------------------------------------------------------------------------
  // Realtime: the merchant's scan reaches this screen through one row update.
  // ---------------------------------------------------------------------------

  const previousStatus = useRef<string | null>(null);

  useEffect(() => {
    if (!resolvedId) return;

    // Coalesce a burst of row events (status, then the event row) into one
    // refetch rather than one request per event.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => reload(), 250);
    };

    const unsubscribe = subscribeToOrder(supabase, resolvedId, {
      onChange: schedule,
      // A socket that has just come back may have been away while the merchant
      // accepted the order or marked it ready, and realtime does not replay what
      // it missed. Reconnecting is the signal to ask for the order again — a
      // full `get_order`, so whatever happened in the gap is picked up whole.
      onStatus: (status) => {
        if (status === 'connected') schedule();
      },
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [resolvedId, reload]);

  // And the page's own waking, which the socket cannot report on: a frozen tab
  // runs no JavaScript, so events that arrived while it slept were delivered but
  // never rendered — and if the socket stayed up throughout, nothing above would
  // fire either. Straight to `reload` rather than through the debounce, because
  // this happens once per wake rather than in bursts.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisible = () => {
      if (!document.hidden) void reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reload]);

  // Announce the handover, which is the one transition the customer is waiting
  // for and may not be looking at the screen for.
  useEffect(() => {
    const status = order.data?.status ?? null;
    if (status && previousStatus.current && previousStatus.current !== status) {
      if (status === 'ready') toast.show(t('customer.order.readyToast'), { tone: 'success' });
      if (status === 'completed') toast.show(t('customer.order.collectedToast'), { tone: 'success' });
      if (status === 'cancelled') toast.show(t('customer.order.cancelledByStaffToast'), { tone: 'danger' });
    }
    previousStatus.current = status;
  }, [order.data?.status, toast, t]);

  // The scheduled branch compares its pickup time against the wall clock, so it
  // needs the same ticking `now` the countdown uses. It sits above the early
  // returns because hooks cannot be conditional.
  const now = useNow();

  const doCancel = useCallback(async () => {
    if (!resolvedId) return;
    const result = await cancel.run(() => cancelMyOrder(supabase, resolvedId, t('customer.order.cancelReasonCustomer')));
    setConfirmingCancel(false);
    if (result) {
      toast.show(t('customer.order.cancelledToast'), { tone: 'neutral' });
      reload();
    }
  }, [resolvedId, cancel, toast, reload, t]);

  /**
   * Closes the order from the customer's phone.
   *
   * The counter often hands the bag over with one hand already full, and the
   * customer's phone is out. The server allows this only from `ready` and only
   * for the order's owner, so this is the same close the merchant would make.
   */
  const doComplete = useCallback(async () => {
    if (!resolvedId) return;
    const result = await complete.run(() => completeMyOrder(supabase, resolvedId));
    if (result) {
      toast.show(t('customer.order.collectedToast'), { tone: 'success' });
      reload();
    }
  }, [resolvedId, complete, toast, reload, t]);

  // ---------------------------------------------------------------------------

  if (resolveError) {
    return (
      <FullScreenMessage
        icon={CircleHelp}
        title={t('customer.order.notFound')}
        message={resolveError}
        actionLabel={t('customer.order.goBack')}
        onAction={() => navigate('/', { replace: true })}
      />
    );
  }

  if (!resolvedId || order.initial) {
    return (
      <Screen>
        <LoadingState message={t('customer.order.loading')} />
      </Screen>
    );
  }

  if (order.error || !order.data) {
    return (
      <Screen>
        <ErrorBanner
          title={t('customer.order.loadFailed')}
          message={order.error ?? t('customer.order.loadFailedHint')}
          onRetry={reload}
        />
      </Screen>
    );
  }

  const data = order.data;
  const steps = pickupSteps(data);
  const isComplete = data.status === 'completed';
  const isCancelled = data.status === 'cancelled';
  const isReady = data.status === 'ready';

  // A scheduled order is judged from the moment the kitchen had to start, not
  // from the pickup time: `prep_start_at` is the pickup time less the prep
  // estimate, so the customer is told while there is still time to cook rather
  // than after the slot has gone. `accept_grace_basis` deliberately does not
  // apply here: anchoring on `placed_at` would flag a 17:00 order at 09:05.
  //
  // `prep_start_at` is null on any order that is not scheduled, and on those
  // placed before the column existed, so the pickup time is the fallback —
  // which is where this used to measure from.
  //
  // `secondsUntil` is negated rather than using `secondsSince`, which floors at
  // zero: with a grace of nought that floor would make the test true before the
  // anchor as well.
  // Which sentence belongs under the pickup time. The rule lives in shared, next
  // to the board's, because it is the same clock read from the other side — see
  // `scheduledNotice`.
  const notice = scheduledNotice(data, now.getTime());

  // Past the grace, and only then, the customer is given something to do about
  // it. Underlined rather than coloured: the sentence it closes is the warning
  // colour, and a link in another hue inside it would read as two thoughts.
  const callForUpdate = data.restaurant_phone ? (
    <Text
      accessibilityRole="link"
      accessibilityLabel={t('common.callRestaurant', {
        phone: data.restaurant_phone,
      })}
      // Size, weight and colour are repeated rather than inherited: `Text` is
      // based on `text-foreground text-base`, so nesting alone left this clause
      // 16pt and near-black inside a 14pt amber sentence.
      className="text-warning text-base font-medium underline"
      onPress={() => openContactUrl(telUrl(data.restaurant_phone!), '_self')}>
      {t('customer.order.callForUpdate')}
    </Text>
  ) : null;

  // `pickupSteps` collapses the cancelled timeline to two entries that share the
  // `placed` key, so the cancelled entry is identified by being the current one.
  const stepLabel = (step: (typeof steps)[number]): string =>
    isCancelled && step.current
      ? t('customer.order.stepCancelled')
      : t(STEP_LABEL_KEYS[step.key]);

  const cancelLabel = t('customer.order.cancelOrder');
  const collectedLabel = t('customer.order.collectedIt');
  const backToMenuLabel = t('customer.order.backToMenu');
  const myOrdersLabel = t('customer.order.myOrders');

  return (
    <Screen
      autoHideHeader
      header={<AppBar title={t('customer.order.screenTitle')} onBack={goBack} />}
      headerGap={HEADER_GAP}
      footer={
        canCustomerCancel(data.status) ? (
          <View className="p-4">
            <Button
              variant="secondary"
              onPress={() => setConfirmingCancel(true)}
              accessibilityLabel={cancelLabel}
              className="w-full">
              <Text>{cancelLabel}</Text>
            </Button>
          </View>
        ) : data.status === 'ready' ? (
          // Offered exactly when the food is on the counter: the merchant may be
          // holding it, and either phone can close the order.
          <View className="p-4">
            <Button
              onPress={() => void doComplete()}
              disabled={complete.pending}
              accessibilityLabel={collectedLabel}
              aria-busy={complete.pending}
              className="w-full">
              <Text>{collectedLabel}</Text>
            </Button>
          </View>
        ) : undefined
      }>
      <Stack gap="xl">
        {/* ---------------------------------------------------------------- */}
        {/* Status and countdown                                              */}
        {/* ---------------------------------------------------------------- */}

        <Card className={cn('p-4', CARD_ACCENT[isReady ? 'success' : isCancelled ? 'danger' : 'brand'])}>
          <Stack gap="lg">
            <Row spread gap="md" align="center">
              {/* The identity column is the one that flexes: with two chips
                  opposite it, a long headline wraps instead of pushing them past
                  the card's edge. `min-w-0` because the CSS default
                  `min-width: auto` refuses to shrink below the content. */}
              <Stack gap="xs" className="min-w-0 flex-1">
                <Text className="text-muted-foreground text-sm">{data.restaurant_name}</Text>
                <Text className="text-xl font-semibold">
                  {isComplete
                    ? t('customer.order.collected')
                    : isCancelled
                      ? t('customer.order.cancelled')
                      : isReady
                        ? t('customer.order.readyTitle')
                        : data.is_scheduled
                          ? t('customer.order.scheduled')
                          : t(HEADLINE_KEYS[data.status])}
                </Text>
              </Stack>
              {/* The money, said only when the counter has taken it. `paid_at` is
                  a fact beside the status, so it rides beside the status chip
                  rather than moving the headline; the shape is the board's own
                  money badge, so the two sides of the counter read the same
                  fact the same way. `Unpaid` is deliberately absent here: the
                  customer pays at the counter, and the chip would otherwise be
                  reporting a till nobody has touched rather than their order. */}
              <Row gap="sm" align="center">
                {data.paid_at ? (
                  <ToneBadge label={t('customer.order.paid')} tone="success" />
                ) : null}
                <StatusChip status={data.status} />
              </Row>
            </Row>

            {!isComplete && !isCancelled ? (
              data.is_scheduled ? (
                <Stack gap="sm">
                  <Text className="text-muted-foreground text-sm font-medium">
                    {t('customer.order.pickupTime')}
                  </Text>
                  <Text className="text-2xl font-semibold tabular-nums">
                    {formatDayTime(data.eta_ready_at, data.timezone, undefined, t)}
                  </Text>
                  {notice === 'notAccepted' || notice === 'notAcceptedSoon' ? (
                    // The slot has been and gone with nobody accepting it. The
                    // pickup time stays — it is what the customer planned around
                    // — and the promise under it is replaced by the sentence the
                    // ASAP branch shows, in the same amber.
                    <Text className={cn('text-base font-medium', TONE_TEXT.warning)}>
                      {notice === 'notAcceptedSoon'
                        ? t('customer.order.notAcceptedSoon')
                        : t('customer.order.notAccepted')}{' '}
                      {callForUpdate}
                    </Text>
                  ) : notice === 'lateBy' ? (
                    // The promise has been broken, so the card counts it — the
                    // same wording and the same clock as the ASAP card.
                    <Text className={cn('text-base font-medium', TONE_TEXT.warning)}>
                      {t('customer.order.lateBy', {
                        when: formatCountdownCoarse(
                          Math.max(1, (now.getTime() - Date.parse(data.eta_ready_at)) / 1000),
                          t,
                        ),
                      })}
                    </Text>
                  ) : notice === 'runningBehind' ? (
                    // Behind, but nothing is late yet: either the kitchen has not
                    // begun and cannot make the slot, or the slot has just gone.
                    // Said at all because this is the moment a customer can still
                    // decide to wait or call.
                    <Text className={cn('text-base font-medium', TONE_TEXT.warning)}>
                      {t('customer.order.runningBehind')}
                    </Text>
                  ) : (
                    <Text className="text-subtle-foreground text-sm">
                      {t('customer.order.pickupTimeHint')}
                    </Text>
                  )}
                </Stack>
              ) : (
                <CountdownProgress
                  target={data.eta_ready_at}
                  totalSeconds={data.eta_seconds}
                  // The kitchen's word, not the clock's, is what turns this
                  // green: an estimate running out means we are late, not that
                  // the food is out.
                  settled={isReady}
                  // `pending` is the order nobody has picked up yet, which is a
                  // different thing to be waiting on from a kitchen running late.
                  accepted={data.status !== 'pending'}
                  // Which anchor the merchant chose for that wait. A pending order
                  // keeps the estimate written at checkout — acceptance is what
                  // re-bases it — so `placed` is the anchor that tells a customer
                  // early rather than after a forty-minute estimate.
                  pendingGraceSeconds={data.restaurant_accept_grace_seconds ?? DEFAULT_ACCEPT_GRACE_SECONDS}
                  pendingGraceFrom={acceptGraceAnchor(data)}
                  // Past the grace, and only then, the customer is given
                  // something to do about it. The number itself is in the
                  // pickup card below — this is the shortcut.
                  lateAction={callForUpdate}
                />
              )
            ) : null}

            {isReady ? (
              <Callout
                tone="success"
                title={t('customer.order.comeToCounter')}
                message={t('customer.order.comeToCounterHint')}
                size="base"
              />
            ) : null}

            {/* Progress timeline */}
            <Stack gap="sm">
              {steps.map((step) => (
                <Row key={`${step.key}-${step.label}`} gap="md" align="center">
                  <View
                    className={cn(
                      'h-[22px] w-[22px] items-center justify-center rounded-full',
                      step.done ? 'bg-success' : step.current ? 'bg-primary' : 'bg-muted'
                    )}>
                    <Text
                      className={cn(
                        'text-xs',
                        step.done || step.current ? 'text-white' : 'text-subtle-foreground'
                      )}>
                      {step.done ? '✓' : step.current ? '●' : ''}
                    </Text>
                  </View>
                  <Stack gap="xs" className="flex-1">
                    <Text
                      className={cn(
                        'text-base',
                        step.current ? 'font-bold' : 'font-normal',
                        step.done || step.current ? 'text-foreground' : 'text-subtle-foreground'
                      )}>
                      {stepLabel(step)}
                    </Text>
                  </Stack>
                  {step.at ? (
                    // `text-sm`, not `text-xs`: these times sit beside 16pt
                    // step labels, and at 12 they were the smallest text on a
                    // screen a customer checks while walking.
                    <Text className="text-subtle-foreground text-sm tabular-nums">
                      {formatDayTime(step.at, data.timezone, undefined, t)}
                    </Text>
                  ) : null}
                </Row>
              ))}
            </Stack>
          </Stack>
        </Card>

        {/* ---------------------------------------------------------------- */}
        {/* Collection code                                                   */}
        {/* ---------------------------------------------------------------- */}

        {!isCancelled ? (
          <Card className="p-4">
            <Stack gap="lg" align="center">
              <Stack gap="xs" align="center">
                <Text className="text-xl font-semibold">
                  {t('customer.order.showWhenCollecting')}
                </Text>
                <Text className="text-muted-foreground text-center text-sm">
                  {t('customer.order.showWhenCollectingHint')}
                </Text>
              </Stack>

              <QrCode
                // A URL, not the bare token: the merchant points a phone camera
                // at this and the browser opens the order. The token alone is
                // only text to a camera, with nothing to tap.
                value={`${import.meta.env.VITE_MERCHANT_URL ?? 'http://localhost:5173'}/pickup/${data.pickup_token}`}
                size={216}
                label={data.code}
              />

              {/* The number the counter calls out, not the code. A hawker reads
                  "forty-six" across the room; the code exists so staff can type
                  the order in, and is shown small for that. */}
              <Text className="text-4xl font-bold tabular-nums">
                {t('customer.order.orderNumber', { number: data.order_number })}
              </Text>

              <Text className="text-subtle-foreground text-center text-sm">
                {t('customer.order.summaryLine', { count: totalItemCount(data) })}
              </Text>

              <OrderCode code={data.code} size="subheading" muted />
            </Stack>
          </Card>
        ) : null}

        {/* ---------------------------------------------------------------- */}
        {/* Order detail                                                      */}
        {/* ---------------------------------------------------------------- */}

        <Section title={t('customer.order.whatYouOrdered')}>
          <Card className="p-4">
            <Stack gap="md">
              {data.items.map((line) => (
                <Row key={line.id} spread gap="md" align="start">
                  <Stack gap="xs" className="flex-1">
                    <Text className="text-base font-semibold">
                      {line.quantity}× {line.name}
                      {line.variant_name_snapshot ? ` · ${line.variant_name_snapshot}` : ''}
                      {(line.options ?? []).length > 0
                        ? ` · ${(line.options ?? []).map((option) => option.name).join(', ')}`
                        : ''}
                    </Text>
                    {line.discount_cents > 0 ? (
                      <Text className="text-success text-sm">
                        {t('customer.order.discountApplied', {
                          label: line.discount_label ?? t('common.discount'),
                        })}
                      </Text>
                    ) : null}
                  </Stack>
                  <Text className="text-base font-semibold tabular-nums">
                    {formatMoney(line.line_total_cents, data.currency)}
                  </Text>
                </Row>
              ))}

              <View className="border-border border-t pt-3">
                <Stack gap="sm">
                  <StatRow
                    label={t('common.subtotal')}
                    value={formatMoney(data.subtotal_cents, data.currency)}
                    numeric
                  />
                  {data.discount_cents > 0 ? (
                    <StatRow
                      label={t('common.discount')}
                      value={`−${formatMoney(data.discount_cents, data.currency)}`}
                      tone="success"
                      numeric
                    />
                  ) : null}
                  {data.tax_cents > 0 ? (
                    <StatRow
                    label={t('common.tax')}
                    value={formatMoney(data.tax_cents, data.currency)}
                    numeric
                  />
                  ) : null}
                  <StatRow
                    label={t('common.total')}
                    value={formatMoney(data.total_cents, data.currency)}
                    emphasis
                    numeric
                    size="lg"
                  />
                </Stack>
              </View>

              {data.note ? (
                <Callout
                  tone="neutral"
                  title={t('customer.order.yourNote')}
                  message={data.note}
                  size="base"
                />
              ) : null}
            </Stack>
          </Card>
        </Section>

        <Section title={t('customer.order.pickupFrom')}>
          <Card className="p-4">
            <Stack gap="sm">
              {/* `text-xl`, the same as the QR card's own title: this is a card
                  heading, and at `text-lg` it was the one card title on the
                  screen sitting at the section heading's size instead.
                  The mark rides with the name here too: this is the card a
                  customer reads while walking to the shop. */}
              <Row gap="sm" align="start">
                <Text className="text-xl font-semibold">{data.restaurant_name}</Text>
                <View className="mt-0.5">
                  <VerifiedMark
                    since={data.restaurant_verified_since}
                    timeZone={data.timezone}
                    size={19}
                  />
                </View>
              </Row>
              {/* The same two lines the menu header carries, so where to walk
                  and who to ring behave the same on both screens — and this is
                  the card a customer reads while walking over. */}
              {data.restaurant_address ? (
                <ContactRow
                  icon={MapPin}
                  label={t('common.openInMaps', { address: data.restaurant_address })}
                  value={data.restaurant_address}
                  url={mapsUrl(data.restaurant_address)}
                  size="base"
                  // Plain, not the menu header's brand colour: this card is read
                  // beside the order's own lines, and a third colour among them
                  // reads as a third kind of thing.
                  variant="plain"
                />
              ) : null}
              {data.restaurant_phone ? (
                <ContactRow
                  icon={Phone}
                  label={t('common.callRestaurant', { phone: data.restaurant_phone })}
                  value={data.restaurant_phone}
                  url={telUrl(data.restaurant_phone)}
                  size="base"
                  variant="plain"
                />
              ) : null}
              {data.customer_email ? (
                <Text className="text-subtle-foreground text-sm">
                  {t('customer.order.receiptSent', { email: data.customer_email })}
                </Text>
              ) : null}
            </Stack>
          </Card>
        </Section>

        {/* A button earns its place here only by naming a destination Back
            cannot reach. An order opened from the list has that list behind it,
            and one just placed has the menu behind it — the checkout took its
            place, so Back pops to it — and neither offers a jump the chevron
            already makes. Only a cold order, opened as the first screen of the
            session from a shared link or a typed code, has nothing behind it and
            names the two places Back would otherwise have to invent. */}
        {cameFromOrders ? null : (
          <Row gap="md" justify="center">
            {menuDepth > 0 ? null : (
              <Button
                variant="ghost"
                // Nothing is behind this order — Back falls back to the list — so
                // the menu takes this screen's place rather than stacking under
                // it.
                onPress={() => navigate(`/r/${data.restaurant_slug}`, { replace: true })}
                accessibilityLabel={backToMenuLabel}>
                <Text>{backToMenuLabel}</Text>
              </Button>
            )}
            <Button
              variant="ghost"
              // The list is never behind an order that offers this row — Back
              // would say so — so opening it stacks nothing, and Back returns
              // the customer to the order they were watching. Taking this
              // screen's place instead would lose it: leaving the order and
              // pressing Back would drop them on the menu.
              onPress={() => navigate('/orders')}
              accessibilityLabel={myOrdersLabel}>
              <Text>{myOrdersLabel}</Text>
            </Button>
          </Row>
        )}
      </Stack>

      <ConfirmDialog
        visible={confirmingCancel}
        title={t('customer.order.cancelTitle')}
        message={t('customer.order.cancelMessage')}
        confirmLabel={t('customer.order.cancelConfirm')}
        cancelLabel={t('customer.order.cancelKeep')}
        destructive
        busy={cancel.pending}
        onConfirm={doCancel}
        onCancel={() => setConfirmingCancel(false)}
      />
    </Screen>
  );
}
